import { ArrowLeft, Check, Plus, RefreshCw } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { daysUntil } from "../../components/home/homeLogic";
import { StampCelebration } from "../../components/passport/StampCelebration";
import { TripHealthUpsell } from "../../components/travels/TripHealthUpsell";
import { ReadinessRing } from "../../components/ui/ReadinessRing";
import {
  AggregatedHealthAdvice,
  getHealthAdvice,
  getReadiness,
  getRecordedVaccineNames,
  ReadinessItem,
  saveReadinessChecklist,
  StampEarned,
  updateReadinessItem,
} from "../../services/api/travelHealth";
import { isVaccineRecorded } from "../../utils/vaccineMatch";
import { getTravel, Travel } from "../../services/api/travels";
import { useAuthStore } from "../../stores/authStore";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { parseDateValue } from "../../utils/dateLocalization";
import { useI18nStore } from "../../stores/i18nStore";
import { paymentRequired } from "../../utils/paymentRequired";
import { toDateOnly } from "../../utils/travelHealthLabels";

/** Generate a base checklist from health advice (fallback if backend returns empty) */
function buildChecklistFromAdvice(
  advice: AggregatedHealthAdvice,
  recordedVaccines: readonly string[],
): ReadinessItem[] {
  const items: ReadinessItem[] = [];
  let seq = 1;

  advice.requiredVaccinations.slice(0, 5).forEach((v) => {
    items.push({
      id: `vac-${seq++}`,
      category: "Vaccinations",
      task: `Get ${v.vaccineName}`,
      // Already on the traveller's record: done before they start.
      completed: isVaccineRecorded(v.vaccineName, recordedVaccines),
      urgent: true,
      source: v.source?.name,
    });
  });
  advice.recommendedVaccinations.slice(0, 3).forEach((v) => {
    items.push({
      id: `vac-${seq++}`,
      category: "Vaccinations",
      task: `Consider ${v.vaccineName}`,
      completed: isVaccineRecorded(v.vaccineName, recordedVaccines),
      urgent: false,
      source: v.source?.name,
    });
  });
  advice.recommendations
    .filter((r) => r.category === "medication")
    .slice(0, 3)
    .forEach((r) => {
      items.push({
        id: `med-${seq++}`,
        category: "Medical",
        task: r.title,
        completed: false,
        urgent: r.severity === "critical" || r.severity === "high",
        source: r.source?.name,
      });
    });
  advice.recommendations
    .filter((r) => r.category === "advisory" || r.category === "warning")
    .slice(0, 2)
    .forEach((r) => {
      items.push({
        id: `adv-${seq++}`,
        category: "Advisory",
        task: r.title,
        completed: false,
        urgent: r.severity === "critical",
        source: r.source?.name,
      });
    });

  // Always add standard items
  items.push(
    {
      id: `ins-${seq++}`,
      category: "Insurance",
      task: "Purchase travel health insurance",
      completed: false,
      urgent: true,
    },
    {
      id: `doc-${seq++}`,
      category: "Documentation",
      task: "Copy passport and vaccination records",
      completed: false,
      urgent: false,
    },
    {
      id: `emer-${seq++}`,
      category: "Emergency",
      task: "Save local emergency contacts",
      completed: false,
      urgent: false,
    },
  );

  return items;
}

export const ReadinessPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const { user } = useAuthStore();
  const [travel, setTravel] = useState<Travel | null>(null);
  const [checklist, setChecklist] = useState<ReadinessItem[]>([]);
  const [newTask, setNewTask] = useState("");
  const [newCategory, setNewCategory] = useState("Custom");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  // The checklist lives on the server once built; until it is saved, a tick
  // would be lost on reload, so the page says so instead of pretending.
  const [saveFailed, setSaveFailed] = useState(false);
  // Set by the save that brought the trip to 100% and earned its stamp.
  const [stampEarned, setStampEarned] = useState<StampEarned | null>(null);
  // Destination advice is a PLUS feature. Without it the checklist is built
  // from the basics, and the page offers PLUS in place — not as a dialog over
  // a checklist the traveller can use for free.
  const [adviceLocked, setAdviceLocked] = useState(false);

  useEffect(() => {
    if (id) loadAll(id);
  }, [id]);

  const loadAll = async (travelId: string) => {
    try {
      setLoading(true);
      const [travelData, saved] = await Promise.all([
        getTravel(travelId),
        getReadiness(travelId).catch(() => null),
      ]);
      setTravel(travelData);

      if (saved && saved.checklist.length > 0) {
        setChecklist(saved.checklist);
      } else {
        let built: ReadinessItem[];
        // No persisted checklist yet – generate from health advice.
        // Both fields are optional on Travel and required by the endpoint.
        try {
          if (!travelData.country || !travelData.departureDate) {
            throw new Error("Trip is missing a destination country or date");
          }
          const [advice, recorded] = await Promise.all([
            getHealthAdvice(
              travelData.country,
              toDateOnly(travelData.departureDate),
              toDateOnly(travelData.returnDate ?? travelData.departureDate),
              travelData.tripType ?? "flight",
              user?.country ?? undefined,
              { quietPaywall: true },
            ),
            getRecordedVaccineNames(),
          ]);
          built = buildChecklistFromAdvice(advice, recorded);
        } catch (adviceError) {
          if (paymentRequired(adviceError)) setAdviceLocked(true);
          // Minimal default checklist
          built = [
            {
              id: "def-1",
              category: "Vaccinations",
              task: "Check destination vaccination requirements",
              completed: false,
              urgent: true,
            },
            {
              id: "def-2",
              category: "Medical",
              task: "Visit travel medicine clinic",
              completed: false,
              urgent: true,
            },
            {
              id: "def-3",
              category: "Insurance",
              task: "Purchase travel health insurance",
              completed: false,
              urgent: true,
            },
            {
              id: "def-4",
              category: "Documentation",
              task: "Copy passport and vaccination records",
              completed: false,
              urgent: false,
            },
            {
              id: "def-5",
              category: "Emergency",
              task: "Save local emergency contacts",
              completed: false,
              urgent: false,
            },
          ];
        }
        setChecklist(built);
        // Saved at once, so every later tick has an item to land on.
        try {
          const saved = await saveReadinessChecklist(travelId, built);
          if (saved.stampEarned) setStampEarned(saved.stampEarned);
        } catch {
          setSaveFailed(true);
        }
      }
    } catch (err: any) {
      console.error("Failed to load readiness data:", err);
    } finally {
      setLoading(false);
    }
  };

  const patchItem = async (
    item: ReadinessItem,
    change: { completed?: boolean; notForMe?: boolean },
  ) => {
    setChecklist((prev) =>
      prev.map((i) => (i.id === item.id ? { ...i, ...change } : i)),
    );
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    try {
      if (id) {
        const result = await updateReadinessItem(id, item.id, change);
        if (result.stampEarned) setStampEarned(result.stampEarned);
      }
    } catch {
      // Revert on error
      setChecklist((prev) => prev.map((i) => (i.id === item.id ? item : i)));
    } finally {
      setSaving((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const toggleItem = (item: ReadinessItem) =>
    patchItem(item, { completed: !item.completed });

  const addCustomTask = async () => {
    if (!newTask.trim()) return;
    const item: ReadinessItem = {
      id: `custom-${Date.now()}`,
      category: newCategory,
      task: newTask.trim(),
      completed: false,
      urgent: false,
    };
    const next = [...checklist, item];
    setChecklist(next);
    setNewTask("");
    if (!id) return;
    try {
      const saved = await saveReadinessChecklist(id, next);
      setSaveFailed(false);
      if (saved.stampEarned) setStampEarned(saved.stampEarned);
    } catch {
      setSaveFailed(true);
    }
  };

  // The same rule as the server's summary (backend tripReadiness.ts), so the
  // ring moves the moment an item is ticked: done over everything that is not
  // marked "not for me".
  const counted = checklist.filter((i) => !i.notForMe);
  const completedCount = counted.filter((i) => i.completed).length;
  const pct =
    counted.length > 0
      ? Math.round((completedCount / counted.length) * 100)
      : null;

  const grouped = checklist.reduce(
    (acc, item) => {
      const bucket = acc[item.category] ?? [];
      bucket.push(item);
      acc[item.category] = bucket;
      return acc;
    },
    {} as Record<string, ReadinessItem[]>,
  );

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl space-y-5" aria-busy="true">
        <div className="h-[220px] animate-pulse rounded-[22px] bg-card" />
        <div className="h-[160px] animate-pulse rounded-[22px] bg-card" />
      </div>
    );
  }

  const days = daysUntil(travel?.departureDate, new Date());
  const when =
    days === null || days < 0
      ? null
      : days === 0
        ? t.homeToday
        : days === 1
          ? t.homeTomorrow
          : t.homeInDays.replace("{n}", String(days));
  const dateRange = [travel?.departureDate, travel?.returnDate]
    .filter(Boolean)
    .map((iso) =>
      (parseDateValue(iso) ?? new Date(NaN)).toLocaleDateString(fmt.locale, {
        day: "numeric",
        month: "short",
      }),
    )
    .join(" – ");
  const place = travel?.destination || travel?.country || "";
  const open = counted.length - completedCount;

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      {/* Destination hero (UX_CONCEPT.md §5.3) */}
      <section
        aria-label={t.readinessChecklist}
        className="rounded-[22px] bg-card p-5 sm:p-6"
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/traveler/travels")}
            aria-label={t.back}
            className="-ml-2 flex h-10 w-10 items-center justify-center rounded-xl text-muted-foreground hover:bg-muted"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
            {t.readinessChecklist}
          </span>
        </div>
        <div className="mt-2 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="truncate font-display text-[30px] font-extrabold leading-tight text-ink sm:text-4xl">
              {place}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {[
                travel?.destination && travel?.country ? travel.country : null,
                dateRange || null,
                when,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
            <div className="mt-4 font-display text-2xl font-bold text-ink">
              {pct === 100
                ? t.readinessAllDone
                : `${open} ${t.readinessStepsLeft}`}
            </div>
            <div className="mt-0.5 text-sm text-muted-foreground">
              {completedCount} / {counted.length} {t.completed}
            </div>
          </div>
          <ReadinessRing
            percent={pct}
            size={120}
            caption={t.readinessCaption}
          />
        </div>
        {saveFailed && (
          <p
            role="alert"
            className="mt-4 text-sm font-semibold text-travel-700"
          >
            {t.readinessSaveFailed}
          </p>
        )}
      </section>

      {stampEarned && (
        <StampCelebration
          earned={stampEarned}
          onClose={() => setStampEarned(null)}
        />
      )}

      {/* Checklist by category */}
      {Object.entries(grouped).map(([category, tasks]) => (
        <section
          key={category}
          aria-label={category}
          className="rounded-[22px] bg-card"
        >
          <div className="flex items-baseline justify-between px-5 pt-5">
            <h2 className="font-display text-lg font-bold text-ink">
              {category}
            </h2>
            <span className="text-xs text-muted-foreground">
              {tasks.filter((x) => x.completed && !x.notForMe).length} /{" "}
              {tasks.filter((x) => !x.notForMe).length}
            </span>
          </div>
          <ul className="mt-2 divide-y divide-border">
            {tasks.map((task) => (
              <li
                key={task.id}
                className={`flex items-start gap-3 px-5 py-3 ${task.notForMe ? "opacity-55" : ""}`}
              >
                <button
                  type="button"
                  onClick={() => toggleItem(task)}
                  disabled={!!saving[task.id] || !!task.notForMe}
                  aria-pressed={task.completed}
                  aria-label={task.task}
                  className="-m-2 flex h-11 w-11 flex-none items-center justify-center rounded-xl"
                >
                  {saving[task.id] ? (
                    <RefreshCw
                      className="h-5 w-5 animate-spin text-muted-foreground"
                      aria-hidden="true"
                    />
                  ) : task.completed ? (
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-health-700 text-white">
                      <Check className="h-4 w-4" aria-hidden="true" />
                    </span>
                  ) : (
                    <span
                      className={`h-6 w-6 rounded-full border-2 ${task.urgent ? "border-travel-500" : "border-slate-300"}`}
                      aria-hidden="true"
                    />
                  )}
                </button>
                <div className="min-w-0 flex-1 pt-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={
                        task.completed
                          ? "text-[15px] text-muted-foreground line-through"
                          : "text-[15px] text-ink"
                      }
                    >
                      {task.task}
                    </span>
                    {task.urgent && !task.completed && !task.notForMe && (
                      <span className="rounded-full bg-travel-100 px-2 py-0.5 text-[11px] font-bold text-travel-800">
                        {t.urgent}
                      </span>
                    )}
                  </div>
                  {task.source && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {t.source}: {task.source}
                    </p>
                  )}
                </div>
                {!task.completed && (
                  <button
                    type="button"
                    onClick={() =>
                      patchItem(task, { notForMe: !task.notForMe })
                    }
                    disabled={!!saving[task.id]}
                    className="min-h-[36px] flex-none rounded-lg px-2 text-xs font-semibold text-muted-foreground hover:bg-muted"
                  >
                    {task.notForMe
                      ? t.readinessCountAgain
                      : t.readinessNotForMe}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}

      {/* Below the list: the free checklist comes first. */}
      {adviceLocked && travel && (
        <TripHealthUpsell country={travel.country ?? ""} />
      )}

      {/* Add a task of one's own */}
      <section className="rounded-[22px] bg-card p-5">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-ink">
          <Plus className="h-5 w-5 text-primary-600" aria-hidden="true" />
          {t.addCustomTask}
        </h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <input
            type="text"
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCustomTask()}
            placeholder={t.taskDescription}
            aria-label={t.taskDescription}
            className="min-h-[48px] min-w-[200px] flex-1 rounded-[14px] border border-input bg-card px-3.5 text-[15px] text-ink focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-600/20"
          />
          <select
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            aria-label={t.category}
            className="min-h-[48px] rounded-[14px] border border-input bg-card px-3 text-[15px] text-ink"
          >
            {[
              "Vaccinations",
              "Medical",
              "Insurance",
              "Documentation",
              "Emergency",
              "Custom",
            ].map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={addCustomTask}
            disabled={!newTask.trim()}
            className="min-h-[48px] rounded-[14px] bg-ink px-5 text-[15px] font-bold text-white disabled:opacity-50"
          >
            {t.add}
          </button>
        </div>
      </section>
    </div>
  );
};
